/**
 * 評価画面のふりかえり（#14）。患者面接と報告をタブで切り替え、
 * 点数 → 総評 → 課題文のタスクごとのカード → 言葉づかい の順に出す。
 */
import { useMemo, useState, type ReactNode } from "react";
import type { ReviewItem, ReviewSide, ReviewTask } from "../lib/evaluation-review";
import { PROGRESS_TARGET_RATIO } from "../lib/interview-task-progress";

const COLORS = {
  surface: "#FFFFFF",
  ink: "#18181B",
  muted: "#71717A",
  sub: "#52525B",
  line: "#E4E4E7",
  soft: "#F4F4F5",
  cyan: "#0E7490",
  cyanSoft: "#ECFEFF",
  green: "#15803D",
  greenSoft: "#DCFCE7",
  orange: "#C2410C",
  orangeSoft: "#FFF7ED",
  orangeLine: "#FED7AA",
  amberInk: "#B45309",
  tealSoft: "#F0FDFA",
  tealLine: "#99F6E4",
  tealInk: "#0F766E",
  rose: "#BE123C",
};

type SideKey = "patient" | "report";

const SIDE_COPY: Record<SideKey, {
  tab: string;
  barLabel: string;
  missingLabel: string;
  termNote: string;
}> = {
  patient: {
    tab: "患者面接",
    barLabel: "聞き取り",
    missingLabel: "まだ聞けていない",
    termNote: "患者さんには、専門用語を使わずふだんの言葉で。",
  },
  report: {
    tab: "報告",
    barLabel: "報告",
    missingLabel: "聞けたのに報告していない",
    termNote: "報告では、ふだんの言葉ではなく専門用語で。",
  },
};

function Badge({ task, side }: { task: ReviewTask; side: SideKey }) {
  const style = (bg: string, color: string) => ({
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 999,
    background: bg,
    color,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap" as const,
    fontVariantNumeric: "tabular-nums" as const,
  });
  if (side === "report" && task.notHeard) return <span style={style(COLORS.soft, COLORS.muted)}>聞けていない</span>;
  if (task.done) return <span style={style(COLORS.greenSoft, COLORS.green)}>★ 達成</span>;
  const prefix = side === "report" ? "報告 " : "";
  const tone = task.got > 0 ? style(COLORS.cyanSoft, COLORS.cyan) : style(COLORS.soft, COLORS.muted);
  if (side === "report" && task.items.some((item) => item.status === "missing")) {
    return <span style={style(COLORS.orangeSoft, COLORS.orange)}>{prefix}{task.got} / {task.total}</span>;
  }
  return <span style={tone}>{prefix}{task.got} / {task.total}</span>;
}

function hasGap(task: ReviewTask): boolean {
  return task.items.some((item) => item.status === "missing");
}

function exampleLabelOf(item: ReviewItem, side: SideKey): string {
  if (side === "report") return "報告の例";
  if (item.level === "proposal") return "提案の例";
  if (item.level === "acceptance_intent") return "確かめ方の例";
  return "聞き方の例";
}

function Hint({ label, texts }: { label: string; texts: string[] }) {
  return (
    <div
      style={{
        fontSize: 12.5,
        lineHeight: 1.7,
        borderRadius: 10,
        padding: "8px 10px",
        background: COLORS.tealSoft,
        border: `1px solid ${COLORS.tealLine}`,
      }}
    >
      <b style={{ color: COLORS.tealInk, marginRight: 6 }}>{label}</b>
      {texts.length === 1 ? (
        texts[0]
      ) : (
        <ol style={{ margin: "4px 0 0", paddingLeft: 20 }}>
          {texts.map((text, index) => (
            <li key={index}>{text}</li>
          ))}
        </ol>
      )}
    </div>
  );
}

function MissingItem({ item, side, communication }: { item: ReviewItem; side: SideKey; communication: boolean }) {
  const [revealed, setRevealed] = useState(false);
  const copy = SIDE_COPY[side];
  // 報告のコミュニケーション（名乗り・言い忘れの確認など）は「聞けたこと」ではないので言い方を変える
  const missingLabel = side === "report" && communication ? "できていない" : copy.missingLabel;
  return (
    <div style={{ borderTop: `1px solid ${COLORS.soft}`, paddingTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 13, fontWeight: 800 }}>
        <span style={{ color: COLORS.orange, marginRight: 6 }}>!</span>
        {item.label}
        <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 700, color: COLORS.muted }}>{missingLabel}</span>
      </div>
      {side === "report" && item.evidence && (
        <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.6 }}>患者面接で聞けた内容：{item.evidence}</div>
      )}
      {item.examples && item.examples.length > 0 && <Hint label={exampleLabelOf(item, side)} texts={item.examples} />}
      {side === "patient" && item.expectedText && (
        revealed ? (
          <div style={{ fontSize: 12.5, lineHeight: 1.7, borderRadius: 10, padding: "8px 10px", background: COLORS.soft }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: COLORS.muted, marginRight: 6 }}>患者の本音</span>
            {item.expectedText}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setRevealed(true)}
            style={{
              alignSelf: "flex-start",
              border: `1px solid ${COLORS.line}`,
              background: COLORS.surface,
              borderRadius: 999,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 800,
              fontFamily: "inherit",
              cursor: "pointer",
              color: COLORS.ink,
            }}
          >
            患者の本音を見る
          </button>
        )
      )}
    </div>
  );
}

function TaskCard({ task, side, defaultOpen }: { task: ReviewTask; side: SideKey; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const done = task.items.filter((item) => item.status === "done");
  const assisted = task.items.filter((item) => item.status === "assisted");
  const missing = task.items.filter((item) => item.status === "missing");
  const numberLabel = task.no > 0 ? String(task.no) : task.no === 0 ? "C" : "他";
  return (
    <div
      data-testid="evaluation-review-task"
      style={{
        background: COLORS.surface,
        border: `1px solid ${open && missing.length > 0 ? COLORS.orangeLine : COLORS.line}`,
        borderRadius: 12,
        padding: "12px 14px",
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          border: "none",
          background: "transparent",
          padding: 0,
          fontFamily: "inherit",
          color: COLORS.ink,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span
          style={{
            width: 26,
            height: 26,
            flexShrink: 0,
            borderRadius: 999,
            display: "grid",
            placeItems: "center",
            fontSize: 12,
            fontWeight: 800,
            color: "#FFFFFF",
            background: task.done ? COLORS.green : task.no > 0 ? COLORS.ink : "#71717A",
          }}
        >
          {numberLabel}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 800, lineHeight: 1.4 }}>{task.title}</span>
        <Badge task={task} side={side} />
        <span aria-hidden="true" style={{ color: COLORS.muted, fontSize: 12 }}>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10 }}>
          {done.length > 0 && (
            <div style={{ fontSize: 12.5, color: COLORS.sub, lineHeight: 1.7 }}>
              <span style={{ color: COLORS.green, fontWeight: 800, marginRight: 6 }}>
                ✓ {side === "report" ? "報告できた" : "聞けた"}
              </span>
              {done.map((item) => item.label).join("・")}
            </div>
          )}
          {assisted.length > 0 && (
            <div style={{ fontSize: 12.5, color: COLORS.sub, lineHeight: 1.7 }}>
              <span style={{ color: COLORS.amberInk, fontWeight: 800, marginRight: 6 }}>△ 聞き返されてから報告</span>
              {assisted.map((item) => item.label).join("・")}
            </div>
          )}
          {missing.map((item) => (
            <MissingItem key={item.itemKey} item={item} side={side} communication={task.no === 0} />
          ))}
          {side === "report" && task.notHeard && (
            <div style={{ fontSize: 12.5, color: COLORS.muted, lineHeight: 1.7 }}>
              患者面接で聞けていない項目なので、報告の対象になっていません。
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreLine({ label, percent }: { label: string; percent: number }) {
  const reached = percent >= PROGRESS_TARGET_RATIO * 100;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 800 }}>{label}</span>
        <span style={{ fontSize: 11, color: COLORS.muted, fontVariantNumeric: "tabular-nums" }}>
          <b style={{ fontSize: 14, color: reached ? COLORS.green : COLORS.ink }}>{percent}%</b>
          {"　"}目安 {Math.round(PROGRESS_TARGET_RATIO * 100)}%
        </span>
      </div>
      <div style={{ position: "relative" }}>
        <div style={{ height: 10, borderRadius: 999, background: COLORS.line, overflow: "hidden" }}>
          <div style={{ width: `${Math.max(0, Math.min(100, percent))}%`, height: "100%", borderRadius: 999, background: reached ? COLORS.green : COLORS.cyan }} />
        </div>
        <div aria-hidden="true" style={{ position: "absolute", left: `${PROGRESS_TARGET_RATIO * 100}%`, top: -3, bottom: -3, width: 2, marginLeft: -1, background: COLORS.ink, opacity: 0.55 }} />
      </div>
    </div>
  );
}

function SideView({
  side,
  review,
  percent,
  summary,
}: {
  side: SideKey;
  review: ReviewSide;
  percent: number | null;
  /** 総評（良かった点・改善できる点）。点数のすぐ下に出す */
  summary?: ReactNode;
}) {
  const copy = SIDE_COPY[side];
  // 抜けのあるタスクを最初の2つだけ開いておく（スマホで長くしすぎない）
  const openTaskNos = useMemo(
    () => new Set(review.tasks.filter(hasGap).slice(0, 2).map((task) => task.no)),
    [review.tasks],
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {percent !== null && <ScoreLine label={copy.barLabel} percent={percent} />}

      {summary}

      <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.15em", color: COLORS.muted, padding: "2px 2px 0" }}>
        課題文ごと
      </div>
      {review.tasks.map((task) => (
        <TaskCard key={`${side}-${task.no}`} task={task} side={side} defaultOpen={openTaskNos.has(task.no)} />
      ))}

      {review.termFindings.length > 0 && (
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.line}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 14, fontWeight: 800 }}>言葉づかい</span>
            <span style={{ padding: "4px 10px", borderRadius: 999, background: COLORS.orangeSoft, color: COLORS.orange, fontSize: 12, fontWeight: 800 }}>
              {review.termFindings.length}件
            </span>
          </div>
          {review.termFindings.map((finding) => (
            <div key={finding.used} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 800 }}>
                <span style={{ color: COLORS.rose, textDecoration: "line-through", textDecorationThickness: 2 }}>{finding.used}</span>
                <span aria-hidden="true">→</span>
                <span style={{ color: COLORS.green }}>{finding.shouldUse}</span>
              </div>
              {finding.utterance && (
                <div style={{ fontSize: 12, color: COLORS.sub, lineHeight: 1.6 }}>あなた：{finding.utterance}</div>
              )}
            </div>
          ))}
          <div style={{ fontSize: 12, color: COLORS.muted }}>{copy.termNote}</div>
        </div>
      )}
    </div>
  );
}

export function EvaluationTaskReview({
  patient,
  report,
  patientPercent,
  reportPercent,
  patientSummary,
  reportSummary,
}: {
  patient: ReviewSide | null;
  report: ReviewSide | null;
  patientPercent: number | null;
  reportPercent: number | null;
  /** 患者面接の総評。患者面接タブに出す */
  patientSummary?: ReactNode;
  /** 報告の総評。報告タブに出す */
  reportSummary?: ReactNode;
}) {
  const [side, setSide] = useState<SideKey>(patient ? "patient" : "report");
  if (!patient && !report) return null;
  const active: SideKey = side === "report" && report ? "report" : patient ? "patient" : "report";
  const review = active === "patient" ? patient! : report!;
  const tabs: Array<{ key: SideKey; percent: number | null }> = [
    ...(patient ? [{ key: "patient" as const, percent: patientPercent }] : []),
    ...(report ? [{ key: "report" as const, percent: reportPercent }] : []),
  ];
  return (
    <section data-testid="evaluation-task-review" style={{ marginBottom: 14, display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ fontSize: 16, fontWeight: 800 }}>ふりかえり</div>
      {tabs.length > 1 && (
        <div
          role="tablist"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 6,
            padding: 4,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 999,
            background: "#FAFAF7",
          }}
        >
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active === tab.key}
              onClick={() => setSide(tab.key)}
              style={{
                height: 42,
                border: "none",
                borderRadius: 999,
                background: active === tab.key ? COLORS.ink : "transparent",
                color: active === tab.key ? "#FFFFFF" : COLORS.muted,
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
                lineHeight: 1.2,
              }}
            >
              {SIDE_COPY[tab.key].tab}
              {tab.percent !== null && <span style={{ display: "block", fontSize: 10.5, fontWeight: 700 }}>{tab.percent}%</span>}
            </button>
          ))}
        </div>
      )}
      <SideView
        key={active}
        side={active}
        review={review}
        percent={active === "patient" ? patientPercent : reportPercent}
        summary={active === "patient" ? patientSummary : reportSummary}
      />
    </section>
  );
}
